import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LayoutComponentManager } from '../../components/LayoutComponentManager';
import { ComponentLibraryModal } from '../../components/ComponentLibraryModal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ToastContainer, useToast } from '../../components/Toast';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { CustomColorsProvider } from '../../contexts/CustomColorsContext';
import { useLayoutEditorState } from './useLayoutEditorState';
import { useLayoutEditorHandlers } from './useLayoutEditorHandlers';
import { useIsMimicking } from './useIsMimicking';
import { useCanUseRoary } from '../../hooks/useCanUseRoary';
import { LoadingState } from './LoadingState';
import { SiteNotFound } from './SiteNotFound';
import { LayoutEditorHeader } from './LayoutEditorHeader';
import { ThemeWarning } from './ThemeWarning';
import { LayoutEditorHint } from './LayoutEditorHint';
import { layoutsApi } from '../../api/modules/layouts';

export const LayoutEditor: React.FC = () => {
  const { siteId, layoutId } = useParams<{ siteId: string; layoutId: string }>();
  const navigate = useNavigate();
  const isMimicking = useIsMimicking();
  const canUseRoaryUi = useCanUseRoary();
  const state = useLayoutEditorState();
  const { toasts, showToast, removeToast } = useToast(canUseRoaryUi);
  const isNewLayout = layoutId === 'new';
  const [isPublishing, setIsPublishing] = useState(false);

  // Derive publish status from the layout data
  const getPublishStatus = useCallback((): 'draft' | 'unpublished_changes' | 'published' => {
    const layout = state.layout;
    if (!layout?.publishedAt) return 'draft';
    if (layout.updatedAt && new Date(layout.updatedAt) > new Date(layout.publishedAt)) {
      return 'unpublished_changes';
    }
    return 'published';
  }, [state.layout]);

  const handlePublishLayout = useCallback(async () => {
    if (!layoutId || isNewLayout || isPublishing) return;
    setIsPublishing(true);
    try {
      const result = await layoutsApi.publish(layoutId);
      // Update the local layout state with the new published data
      if (result.layout) {
        state.setLayout(result.layout);
      }
      const parts: string[] = ['Layout published'];
      if (result.republishedPageIds?.length) {
        parts.push(`${result.republishedPageIds.length} page(s) republished`);
      }
      if (result.skippedNeverPublishedPageIds?.length) {
        parts.push(`${result.skippedNeverPublishedPageIds.length} draft-only page(s) unaffected`);
      }
      showToast(parts.join('. '), 'success');
    } catch (err: any) {
      showToast(
        err?.response?.data?.message || err?.message || 'Failed to publish layout',
        'error'
      );
    } finally {
      setIsPublishing(false);
    }
  }, [layoutId, isNewLayout, isPublishing, showToast, state]);

  const handlers = useLayoutEditorHandlers({
    siteId,
    layoutId,
    isNewLayout,
    state,
    showToast,
  });

  useEffect(() => {
    if (!siteId) {
      navigate('/dashboard');
      return;
    }
    handlers.loadLayoutData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, layoutId, navigate]);

  // Watch for theme or brand package changes and reload colors
  const siteBrandPackageId = (state.site as any)?.brandPackageId;
  useEffect(() => {
    // Reload colors when theme or brand package changes
    if (state.site?.theme || siteBrandPackageId) {
      handlers.loadThemeColors(state.site?.theme || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.site?.theme, siteBrandPackageId]);

  if (state.isLoading) {
    return <LoadingState />;
  }

  if (!state.site) {
    return <SiteNotFound />;
  }

  const handleLayoutNameChange = (name: string) => {
    state.setLayoutName(name);
    state.setHasUnsavedChanges(true);
  };

  const handleLayoutDescriptionChange = (description: string) => {
    state.setLayoutDescription(description);
    state.setHasUnsavedChanges(true);
  };

  const handleIsDefaultChange = (isDefault: boolean) => {
    state.setIsDefault(isDefault);
    state.setHasUnsavedChanges(true);
  };

  return (
    <ThemeProvider theme={state.site.theme || 'standard'}>
      <CustomColorsProvider customColors={state.themeColors || {}}>
        <div className={`min-h-screen bg-gray-100 ${isMimicking ? 'mt-[52px]' : ''}`}>
          <LayoutEditorHeader
            siteId={siteId!}
            layoutName={state.layoutName}
            layoutDescription={state.layoutDescription}
            hasUnsavedChanges={state.hasUnsavedChanges}
            isDefault={state.isDefault}
            isSaving={state.isSaving}
            isNewLayout={isNewLayout}
            publishStatus={getPublishStatus()}
            isPublishing={isPublishing}
            onLayoutNameChange={handleLayoutNameChange}
            onLayoutDescriptionChange={handleLayoutDescriptionChange}
            onIsDefaultChange={handleIsDefaultChange}
            onSave={handlers.handleSaveLayout}
            onPublish={handlePublishLayout}
          />

          {state.themeError && <ThemeWarning error={state.themeError} />}

          <main className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <LayoutEditorHint />

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <LayoutComponentManager
                  components={state.components}
                  onComponentsChange={handlers.handleComponentsChange}
                  onAddComponent={position => {
                    state.setInsertPosition(position !== undefined ? position : null);
                    state.setShowComponentLibraryModal(true);
                  }}
                  onDeleteComponent={handlers.handleDeleteComponent}
                  siteId={siteId}
                />
              </div>
            </div>
          </main>

          <ComponentLibraryModal
            isOpen={state.showComponentLibraryModal}
            onClose={() => {
              state.setShowComponentLibraryModal(false);
              state.setInsertPosition(null);
            }}
            onSelectTemplate={componentType => {
              handlers.handleAddComponent(componentType as string);
            }}
            onSelectLibraryComponent={handlers.handleAddLibraryComponent}
            siteId={siteId!}
            isLoading={handlers.isAddingComponent}
            isLayoutMode={true}
          />

          <ConfirmModal
            isOpen={state.deleteModal.isOpen}
            onClose={() => state.setDeleteModal({ isOpen: false, component: null })}
            onConfirm={handlers.confirmDeleteComponent}
            title="Delete Component"
            message={`Are you sure you want to delete this ${state.deleteModal.component?.type} component? This action cannot be undone.`}
            confirmText="Delete"
            type="danger"
          />

          <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
      </CustomColorsProvider>
    </ThemeProvider>
  );
};

export default LayoutEditor;
